import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { BuildingIcon, SaveIcon, TriangleAlertIcon } from "lucide-react";

import { asMutation, initUseQuery } from "simplydo/core/query";
import { getUserDomain } from "simplydo/core/users";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { List, ListItem } from "simplydo/react/ui/list";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import {
  updateCurrentUser,
  useCurrentUser,
  userCache,
} from "utils/currentUser";
import util from "utils/utils";

import { BusinessProfileSearcher } from "components/ideas/Components/IdeaBusinessProfile/BusinessProfileSearcher";

import { EditOverview } from "../IdeaBusinessProfileModal/Tabs/EditOverview";
import type { IdeaBusinessProfileRef } from "../IdeaBusinessProfileModal/types";
import type { CreateProfilePayload } from "./types";

const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  .company-info {
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    span {
      display: block;
      margin: 0;
    }
    .description {
      opacity: 0.7;
    }
  }
`;

const StyledCoverImage = styled.div<{ $image?: string }>`
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "120px")};
  min-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "120px")};
  max-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  max-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  margin-right: 15px;
  background-position: center center;
  background-color: white;
  background-image: ${({ $image }) => ($image ? `url(${$image})` : "none")};
  background-size: contain;
  background-repeat: no-repeat;
`;

const CreateBusinessProfile = () => {
  const user = useCurrentUser();
  const userDomain = user ? getUserDomain(user) : "";

  const { t } = useTranslation();
  const theme = useTheme();

  const [searcherOpen, setSearcherOpen] = useState(false);

  const [newProfile, setNewProfile] = useState<IdeaBusinessProfileRef | null>(
    null,
  );
  const newProfileQuery = useQuery(
    initUseQuery(api.users.getProfile, [newProfile?._id ?? ""], {
      isEnabled: !!newProfile?._id,
    }),
  );

  const profileQuery = useQuery(initUseQuery(api.users.getProfiles, []));

  const updateProfileMutation = useMutation({
    mutationKey: [
      "post",
      "users",
      "profiles",
      newProfileQuery.data?.ideaBusinessProfile?._id,
    ],
    mutationFn: asMutation(api.users.updateProfile)!,
    onSuccess: ({ ideaBusinessProfile }) => {
      api.invalidateQueries(api.users.getProfile, [ideaBusinessProfile._id]);
    },
    onError: () => {
      toast.error("Failed to update profile");
    },
  });

  const createProfileMutation = useMutation({
    mutationKey: ["post", "users", "profiles"],
    mutationFn: asMutation(api.users.createProfile),
    onSuccess: (data: OpenAPI.PUT<"/users/profiles">["response"]) => {
      userCache.updatePermissions(data.ideaBusinessProfile._id, [
        "ideaBusinessProfile.viewDetails",
        "ideaBusinessProfile.editDetails",
        "ideaBusinessProfile.viewMembers",
        "ideaBusinessProfile.editMembers",
        "ideaBusinessProfile.deleteProfile",
      ]);
    },
    onError: () => {
      toast.error("Failed to create profile");
    },
  });

  const joinProfileMutation = useMutation({
    mutationKey: ["post", "users", "profiles", "join"],
    mutationFn: asMutation(api.users.joinProfile),
    onSuccess: () => {},
    onError: () => {
      toast.error("Failed to join profile");
    },
  });

  const joinProfile = useCallback(
    (profile: IdeaBusinessProfileRef) => {
      joinProfileMutation.mutate([profile._id], {
        onSuccess: () => {
          // Invalidate the profiles query to refetch the updated list of profiles
          api.invalidateQueries(api.users.me);
          api.invalidateQueries(api.users.getProfiles);
          setNewProfile(profile);
        },
      });
    },
    [joinProfileMutation],
  );

  const createBusinessProfile = useCallback(
    (profile: CreateProfilePayload) => {
      createProfileMutation.mutate([profile], {
        onSuccess: ({ ideaBusinessProfile }) => {
          setSearcherOpen(false);
          setNewProfile(ideaBusinessProfile);
        },
      });
    },
    [createProfileMutation],
  );

  if (!user || profileQuery.isLoading) {
    return <Loader />;
  }

  const {
    name,
    address = {},
    creditsafeRegNo,
    foundingYear,
    vatNo,
    phoneNumber,
    websiteUrl,
    websiteUrlSkipped,
    type,
    size,
    vatNoSkipped,
    creditsafeRegNoSkipped,
  } = newProfileQuery?.data?.ideaBusinessProfile || {};

  const { country, city, streetAddress, postcode, region } = address || {};

  const currentYear = new Date().getFullYear();
  const requiredInfoFields = [
    { label: "Name", value: name },
    {
      label: "Your business website",
      value: websiteUrl,
      skipped: websiteUrlSkipped,
    },
    { label: "Phone number", value: phoneNumber },
    { label: "Street address", value: streetAddress },
    { label: "Country", value: country },
    { label: "Region", value: region, skipped: country !== "GB" },
    { label: "City", value: city },
    { label: "Postcode", value: postcode },
    { label: "Organisation size", value: size },
    { label: "Organisation type", value: type },
    {
      label: "Organisation founding year",
      value:
        foundingYear && foundingYear > 999 && foundingYear <= currentYear
          ? foundingYear
          : "",
    },
    {
      label: "Organisation registration number",
      value: creditsafeRegNo,
      skipped: creditsafeRegNoSkipped,
    },
    { label: "VAT registration number", value: vatNo, skipped: vatNoSkipped },
  ];
  const isMissingRequiredInfo = requiredInfoFields.some(
    (field) => !field.value && !field.skipped,
  );

  return (
    <div style={{ paddingLeft: 2 }}>
      <div
        style={{
          display: theme.sizes.isMobile ? "grid" : "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <StyledCoverImage
          $image={
            user?.groupRequiringProfile?.coverImageUrl ||
            user?.ownerOrganisation?.darkLogoUrl
          }
        />
        <div>
          <H3>
            Please tell us which organisation you belong to
            <Subheader>
              In order to proceed, you need to associate yourself with an
              organisational profile. If one already exists for you, we’ll
              suggest it below. Otherwise, you can create a new one.
            </Subheader>
          </H3>
        </div>
      </div>

      {newProfile && newProfileQuery.isPending ? (
        <Loader />
      ) : newProfileQuery?.data?.ideaBusinessProfile ? (
        <div>
          <H3>
            Update details
            <Subheader>
              Please complete the information below to represent your
              organisation in {util.appName()}. This information is used to
              identify your organisation and to provide context for your{" "}
              {t("generic.ideas")}.
            </Subheader>
          </H3>

          <EditOverview
            businessProfile={newProfileQuery?.data?.ideaBusinessProfile}
          />
          <Divider />
          {isMissingRequiredInfo ? (
            <Message color="orange" size="small">
              <Icon icon={TriangleAlertIcon} />
              <MessageHeader>
                Your organisation profile is missing required information
              </MessageHeader>
              <MessageContent>
                <p>
                  The following parts of your organisation&apos;s profile are
                  still missing or incomplete. Please make sure to complete them
                  before submitting your {t("generic.idea")}.
                </p>
                <List variant="bulleted" compact>
                  {requiredInfoFields.map((field) => {
                    if (field.value || field.skipped) {
                      return null;
                    }
                    return <ListItem key={field.label}>{field.label}</ListItem>;
                  })}
                </List>
              </MessageContent>
            </Message>
          ) : null}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={isMissingRequiredInfo}
              onClick={() => {
                if (newProfile && newProfileQuery.data?.ideaBusinessProfile) {
                  const { ownerTags: _ownerTags, ...profileUpdate } =
                    newProfileQuery.data.ideaBusinessProfile;
                  updateProfileMutation.mutate([newProfile._id, profileUpdate]);
                  updateCurrentUser({ groupRequiringProfile: undefined });
                }
                setNewProfile(null);
              }}
              color="primary"
            >
              <Icon icon={SaveIcon} />
              Save profile & continue
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {userDomain &&
          profileQuery.data?.joinableProfiles &&
          profileQuery.data.joinableProfiles.length !== 0 ? (
            <>
              <Divider />
              <div style={{ flex: 1 }}>
                <H3>
                  Join an existing profile
                  <Subheader>
                    Based on your email address, we’ve matched you with the
                    following organisations. You can select one of these instead
                    of creating one from scratch.
                  </Subheader>
                </H3>
                <Grid mobile={1} columns={2}>
                  {profileQuery.data?.joinableProfiles?.map((profile) => (
                    <GridColumn key={profile._id}>
                      <Card
                        key={profile._id}
                        fluid
                        style={{ margin: 0, marginTop: 10 }}
                      >
                        <CardContent>
                          <ItemContainer>
                            {profile?.imageUrl ? (
                              <Image
                                src={profile?.imageUrl}
                                style={{
                                  borderRadius: 10,
                                  overflow: "hidden",
                                  height: 50,
                                  width: 50,
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Icon icon={BuildingIcon} size="large" />
                            )}
                            <div className="company-info">
                              <H5 style={{ margin: 0 }}>{profile?.name}</H5>
                              <p style={{ margin: 0 }}>
                                {profile?.address?.city
                                  ? `${profile?.address?.city || ""}`
                                  : null}
                              </p>
                              <p style={{ margin: 0 }}>
                                {profile?.websiteUrl
                                  ? `${profile?.websiteUrl || ""}`
                                  : null}
                              </p>
                            </div>
                          </ItemContainer>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: 10,
                            }}
                          >
                            <Button
                              onClick={() => joinProfile(profile)}
                              color="primary"
                            >
                              Join this profile
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </GridColumn>
                  ))}
                </Grid>
              </div>
              <Divider horizontal>or</Divider>
            </>
          ) : null}
          {profileQuery.data?.profiles &&
          profileQuery.data.profiles.length > 0 ? (
            <div style={{ flex: 1 }}>
              <H3>
                Finalise a profile
                <Subheader>
                  Review your profile information and make any necessary
                  changes.
                </Subheader>
              </H3>
              <Grid mobile={1} columns={2}>
                {profileQuery.data?.profiles?.map((profile) => (
                  <GridColumn key={profile._id}>
                    <Card
                      key={profile._id}
                      fluid
                      style={{ margin: 0, marginTop: 10 }}
                    >
                      <CardContent>
                        <ItemContainer>
                          {profile?.imageUrl ? (
                            <Image
                              src={profile?.imageUrl}
                              style={{
                                borderRadius: 10,
                                overflow: "hidden",
                                height: 50,
                                width: 50,
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <Icon icon={BuildingIcon} size="large" />
                          )}
                          <div className="company-info">
                            <H5 style={{ margin: 0 }}>{profile?.name}</H5>
                            <p style={{ margin: 0 }}>
                              {profile?.address?.city
                                ? `${profile?.address?.city || ""}`
                                : null}
                            </p>
                            <p style={{ margin: 0 }}>
                              {profile?.websiteUrl
                                ? `${profile?.websiteUrl || ""}`
                                : null}
                            </p>
                          </div>
                        </ItemContainer>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 10,
                          }}
                        >
                          <Button
                            onClick={() => {
                              setNewProfile(profile);
                            }}
                            color="primary"
                          >
                            Edit profile
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </GridColumn>
                ))}
              </Grid>
              <Divider section />
            </div>
          ) : null}
          <div style={{ flex: 1 }}>
            <H3>
              Create a new profile for your organisation
              <Subheader>
                Register your organisation by creating and joining a new
                profile.
              </Subheader>
            </H3>
            <Button
              onClick={() => {
                if (
                  profileQuery?.data?.joinableProfiles &&
                  profileQuery?.data?.joinableProfiles?.length !== 0
                ) {
                  util
                    .confirm(
                      "Confirm creating a new profile",
                      "We were able to identify an existing profile that you can join based on your email address. Are you sure you want to create a new profile instead of joining an existing one?",
                    )
                    .then(() => {
                      setSearcherOpen(true);
                    })
                    .catch(() => {});
                } else {
                  setSearcherOpen(true);
                }
              }}
              color={
                !profileQuery?.data?.joinableProfiles ||
                profileQuery?.data?.joinableProfiles?.length === 0
                  ? "primary"
                  : undefined
              }
            >
              Create a new organisational profile
            </Button>
            {searcherOpen ? (
              <BusinessProfileSearcher
                forType="org"
                forId={user.ownerOrganisation?._id ?? ""}
                isCreating={createProfileMutation.isPending}
                onSelect={(profile) => {
                  createBusinessProfile(profile as CreateProfilePayload);
                }}
                onClose={() => {
                  setSearcherOpen(false);
                }}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBusinessProfile;
